import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FolderOpen, Search, Eye, Download, Trash2, FileText, Image as ImageIcon, Files, X } from "lucide-react";
import { api, API, fmtDate, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const iso = (d) => d.toISOString().slice(0, 10);
const fyStart = (d) => new Date(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1, 3, 1);
const RANGES = {
  all: () => ["", ""],
  today: () => [iso(new Date()), iso(new Date())],
  yesterday: () => { const d = new Date(); d.setDate(d.getDate() - 1); return [iso(d), iso(d)]; },
  this_week: () => { const t = new Date(); const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [iso(s), iso(t)]; },
  this_month: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(new Date(t.getFullYear(), t.getMonth() + 1, 0))]; },
  prev_month: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))]; },
  fy: () => { const s = fyStart(new Date()); return [iso(s), iso(new Date(s.getFullYear() + 1, 2, 31))]; },
};
const RANGE_LABELS = { all: "All Time", today: "Today", yesterday: "Yesterday", this_week: "This Week", this_month: "This Month", prev_month: "Previous Month", fy: "Financial Year", custom: "Custom Range" };

const MODULE_ROUTE = {
  "Purchase Bill": "/purchase-bills", "Purchase Order": "/purchase-orders", "Sales Invoice": "/sales",
  "Sales Return": "/sales-returns", Quotation: "/quotations", "Sales Order": "/sales", Supplier: "/suppliers",
  Customer: "/customers", "QC / LAB": "/qc-tests", "Sample Dispatch": "/samples", Production: "/production",
  "Job Work": "/job-work", Inventory: "/inventory", "Stock Transfer": "/inventory", Request: "/requests",
  Payments: "/payments", "Accounts / Expense": "/expenses", Bank: "/bank", Dispatch: "/dispatch",
  Assets: "/assets", "Company Settings": "/company",
};

const Stat = ({ label, value, icon: Icon, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </div>
    <div className="text-xl font-semibold tabular-nums mt-1 text-slate-900">{value}</div>
  </div>
);

export default function DocumentsLibrary() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("all");
  const [docType, setDocType] = useState("all");
  const [uploader, setUploader] = useState("all");
  const [fileType, setFileType] = useState("all");
  const [rangeKey, setRangeKey] = useState("all");
  const [range, setRange] = useState(["", ""]);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);

  const [start, end] = range;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/documents", {
        params: {
          q, module: module === "all" ? "" : module, doc_type: docType === "all" ? "" : docType,
          uploaded_by: uploader === "all" ? "" : uploader, file_type: fileType === "all" ? "" : fileType,
          start, end,
        },
      });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [q, module, docType, uploader, fileType, start, end]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const pickRange = (k) => { setRangeKey(k); if (k !== "custom") setRange(RANGES[k]()); };
  const token = localStorage.getItem("token");
  const url = (id) => `${API}/files/download/${id}?token=${token}`;

  const openDoc = (d) => {
    if (d.file_type === "Image") setPreview(d);
    else window.open(url(d.id), "_blank");
  };
  const openSource = (d) => {
    const route = MODULE_ROUTE[d.module];
    if (!route) return toast.info("This document is not linked to a transaction");
    if (!hasPerm(user, d.rbac_module, "view")) return toast.error("You don't have permission to open this transaction");
    nav(route);
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}? The ${d.module} record itself is not deleted.`)) return;
    try { await api.delete(`/documents/file/${d.id}`); toast.success("Document deleted"); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const clearFilters = () => { setQ(""); setModule("all"); setDocType("all"); setUploader("all"); setFileType("all"); setRangeKey("all"); setRange(["", ""]); };

  const rows = data?.documents || [];
  const stats = data?.stats;
  const byModule = useMemo(() => (stats?.by_module || []).slice(0, 8), [stats]);

  return (
    <div data-testid="documents-library">
      <div className="flex items-center gap-2 mb-5">
        <FolderOpen className="w-6 h-6 text-[#0B132B]" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Documents</h1>
          <p className="text-sm text-slate-500 mt-0.5">Every file uploaded anywhere in the ERP — you only see documents from modules you can access.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Stat testid="docs-stat-total" label="Total Documents" value={stats?.total ?? "—"} icon={Files} />
        <Stat testid="docs-stat-month" label="This Month" value={stats?.this_month ?? "—"} />
        <Stat testid="docs-stat-pdf" label="PDFs" value={stats?.pdfs ?? "—"} icon={FileText} />
        <Stat testid="docs-stat-images" label="Images" value={stats?.images ?? "—"} icon={ImageIcon} />
        <Stat testid="docs-stat-other" label="Other Files" value={stats?.others ?? "—"} />
      </div>

      {!!byModule.length && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="docs-by-module">
          {byModule.map((m) => (
            <button key={m.module} data-testid={`docs-module-chip-${m.module}`} onClick={() => setModule(m.module === module ? "all" : m.module)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${module === m.module ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {m.module}: <b>{m.count}</b>
            </button>))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="docs-search" placeholder="Search name, reference, party, user..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 bg-white h-9 w-72" />
        </div>
        <Select value={module} onValueChange={setModule}>
          <SelectTrigger data-testid="docs-filter-module" className="w-44 bg-white h-9"><SelectValue placeholder="Module" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Modules</SelectItem>{(data?.modules || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger data-testid="docs-filter-type" className="w-44 bg-white h-9"><SelectValue placeholder="Document Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Document Types</SelectItem>{(data?.doc_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={uploader} onValueChange={setUploader}>
          <SelectTrigger data-testid="docs-filter-user" className="w-40 bg-white h-9"><SelectValue placeholder="Uploaded By" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Users</SelectItem>{(data?.users || []).map((u) => <SelectItem key={u.id || u.name} value={u.id || u.name}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fileType} onValueChange={setFileType}>
          <SelectTrigger data-testid="docs-filter-filetype" className="w-32 bg-white h-9"><SelectValue placeholder="File Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Files</SelectItem><SelectItem value="PDF">PDF</SelectItem><SelectItem value="Image">Image</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
        </Select>
        <Select value={rangeKey} onValueChange={pickRange}>
          <SelectTrigger data-testid="docs-filter-range" className="w-40 bg-white h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(RANGE_LABELS).map((k) => <SelectItem key={k} value={k}>{RANGE_LABELS[k]}</SelectItem>)}</SelectContent>
        </Select>
        {rangeKey === "custom" && (
          <>
            <Input data-testid="docs-start" type="date" value={start} onChange={(e) => setRange([e.target.value, end])} className="bg-white h-9 w-40" />
            <Input data-testid="docs-end" type="date" value={end} onChange={(e) => setRange([start, e.target.value])} className="bg-white h-9 w-40" />
          </>
        )}
        <Button data-testid="docs-clear" variant="outline" className="rounded-sm h-9 gap-1" onClick={clearFilters}><X className="w-3.5 h-3.5" /> Clear</Button>
        <span className="text-sm text-slate-500 ml-auto" data-testid="docs-count">{rows.length} document(s)</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            {["Document Name", "Document Type", "Module", "Reference", "Related Party", "Uploaded By", "Upload Date", "File Type", "Action"].map((h) => (
              <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400" data-testid="docs-loading">Loading documents...</td></tr>
              : !rows.length ? <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400" data-testid="docs-empty">No documents match these filters</td></tr>
                : rows.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 max-w-[260px] truncate">
                      <button data-testid={`doc-detail-${d.id}`} className="text-slate-800 hover:text-[#0B132B] hover:underline text-left" onClick={() => setDetail(d)}>{d.name}</button>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap"><Badge variant="outline" className="rounded-full px-2 text-[11px]">{d.doc_type}</Badge></td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{d.module}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {d.reference !== "-" ? <button data-testid={`doc-ref-${d.id}`} className="text-[#0B132B] hover:underline font-medium" onClick={() => openSource(d)}>{d.reference}</button> : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">{d.party}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{d.uploaded_by}<span className="text-[10px] text-slate-400 ml-1">{d.uploaded_by_role !== "-" ? d.uploaded_by_role : ""}</span></td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(d.upload_date)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{d.format}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button data-testid={`doc-view-${d.id}`} size="icon" variant="ghost" className="h-8 w-8" title="View" onClick={() => openDoc(d)}><Eye className="w-4 h-4" /></Button>
                      <a data-testid={`doc-download-${d.id}`} href={url(d.id)} download={d.name} className="inline-flex items-center justify-center h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Download"><Download className="w-4 h-4" /></a>
                      {d.can_delete && <Button data-testid={`doc-delete-${d.id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Delete" onClick={() => remove(d)}><Trash2 className="w-4 h-4" /></Button>}
                    </td>
                  </tr>))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="doc-detail-dialog">
          <DialogHeader><DialogTitle className="font-head break-all">{detail?.name}</DialogTitle>
            <DialogDescription>Source details for this document.</DialogDescription></DialogHeader>
          <div className="space-y-1.5 text-sm">
            {[["Source Module", detail?.module], ["Reference Number", detail?.reference], ["Related Party", detail?.party],
              ["Document Type", detail?.doc_type], ["Uploaded By", `${detail?.uploaded_by || "-"} (${detail?.uploaded_by_role || "-"})`],
              ["Upload Date / Time", detail?.uploaded_at ? String(detail.uploaded_at).replace("T", " ").slice(0, 19) : "-"],
              ["File Type", detail?.format]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-1"><span className="text-slate-500">{k}</span><span className="text-slate-800 text-right break-all">{v || "-"}</span></div>))}
          </div>
          <DialogFooter className="gap-2">
            {detail?.can_delete && <Button data-testid="doc-detail-delete" variant="outline" className="rounded-sm text-red-600 border-red-200" onClick={() => remove(detail)}>Delete</Button>}
            <Button data-testid="doc-detail-view" variant="outline" className="rounded-sm gap-1.5" onClick={() => openDoc(detail)}><Eye className="w-4 h-4" /> View</Button>
            <Button className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => window.open(url(detail.id), "_blank")}><Download className="w-4 h-4" /> Download</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="doc-image-preview">
          <DialogHeader><DialogTitle className="font-head break-all text-base">{preview?.name}</DialogTitle></DialogHeader>
          {preview && <img src={url(preview.id)} alt={preview.name} className="w-full max-h-[70vh] object-contain rounded-sm border border-slate-200" />}
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setPreview(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
